import { useState } from 'react';
import type { ImportReplaceDiff, ImportReplacePerson } from '@ni/shared';
import {
  IMPORT_REPLACE_ADDED,
  IMPORT_REPLACE_ADDED_NOTE,
  IMPORT_REPLACE_DETAILS,
  IMPORT_REPLACE_DETAILS_KEPT,
  IMPORT_REPLACE_DETAILS_RESCAN,
  IMPORT_REPLACE_DROPPED,
  IMPORT_REPLACE_DROPPED_RAISED,
  IMPORT_REPLACE_DROPPED_RAISED_NOTE,
  IMPORT_REPLACE_ON_RECORD,
  IMPORT_REPLACE_RESCAN,
  IMPORT_REPLACE_SEATS,
  importReplaceCounts,
  importReplaceHeadline,
  importReplaceMore,
  importReplaceTakeOff,
  importReplaceToConfirm,
} from '../../lib/copy';

/** How many of a group show before "and N more". */
const SHOWN = 5;

/**
 * **What replacing the list does** (docs/notes/2026-09-30-replace-attendee-list-spec.md),
 * collapsed to a headline and one line of counts. Open, the groups run in order
 * of what is at stake, five names each; the one question is the promoted
 * attendees the new file drops, who stay unless ticked.
 */
export function ReplaceDiffBlock({
  diff,
  dropped,
  disabled,
  onToggle,
}: {
  diff: ImportReplaceDiff;
  /** The promoted attendees the office has ticked to take off. */
  dropped: readonly string[];
  disabled: boolean;
  onToggle: (nodeId: string, checked: boolean) => void;
}) {
  return (
    <details className="replace-diff">
      <summary>
        <span className="replace-head">
          <span className="replace-headline">
            {importReplaceHeadline(
              diff.before,
              diff.after -
                diff.on_record.filter((one) => one.node_id && dropped.includes(one.node_id)).length,
            )}
          </span>
          <span className="src">{importReplaceCounts(diff)}</span>
        </span>
        {diff.on_record.length > 0 ? (
          <span className="replace-ask">{importReplaceToConfirm(diff.on_record.length)}</span>
        ) : null}
      </summary>

      {diff.on_record.length > 0 ? (
        <Group title={IMPORT_REPLACE_ON_RECORD} count={diff.on_record.length}>
          {diff.on_record.map((one) => (
            <div key={one.node_id} className="replace-row">
              <Line person={one} />
              <label className="replace-take-off">
                <input
                  type="checkbox"
                  checked={one.node_id !== null && dropped.includes(one.node_id)}
                  disabled={disabled || one.node_id === null}
                  onChange={(event) =>
                    one.node_id && onToggle(one.node_id, event.currentTarget.checked)
                  }
                />
                <span>{importReplaceTakeOff(one.name)}</span>
              </label>
            </div>
          ))}
        </Group>
      ) : null}
      <Rows
        title={IMPORT_REPLACE_DROPPED_RAISED}
        people={diff.dropped_raised}
        note={IMPORT_REPLACE_DROPPED_RAISED_NOTE}
      />
      <Names title={IMPORT_REPLACE_DROPPED} people={diff.dropped} />
      <Names title={IMPORT_REPLACE_ADDED} people={diff.added} note={IMPORT_REPLACE_ADDED_NOTE} />
      <Rows title={IMPORT_REPLACE_SEATS} people={diff.seats} />
      <Rows
        title={IMPORT_REPLACE_DETAILS}
        people={diff.details}
        note={
          diff.details.some((one) => one.rescan)
            ? IMPORT_REPLACE_DETAILS_RESCAN
            : IMPORT_REPLACE_DETAILS_KEPT
        }
      />
    </details>
  );
}

function Group({
  title,
  count,
  note,
  children,
}: {
  title: string;
  count: number;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="replace-group">
      <div className="replace-group-head">
        <span>{title}</span>
        <span className="src">{count}</span>
      </div>
      {children}
      {note ? <div className="src">{note}</div> : null}
    </div>
  );
}

/** One person with their standing and what the group says about them. */
function Line({ person }: { person: ImportReplacePerson }) {
  return (
    <div className="replace-line">
      <span>
        {person.name}
        {person.standing ? (
          <span className={`replace-standing ${person.standing.tone}`}>
            {person.standing.label}
          </span>
        ) : null}
      </span>
      {person.detail ? (
        <span className="src">
          {person.rescan ? `${person.detail} · ${IMPORT_REPLACE_RESCAN}` : person.detail}
        </span>
      ) : null}
    </div>
  );
}

/** A group read person by person: five, then the rest on request. */
function Rows({
  title,
  people,
  note,
}: {
  title: string;
  people: readonly ImportReplacePerson[];
  note?: string;
}) {
  const [all, setAll] = useState(false);
  if (people.length === 0) return null;
  const shown = all ? people : people.slice(0, SHOWN);
  return (
    <Group title={title} count={people.length} note={note}>
      {shown.map((one, at) => (
        <Line key={one.node_id ?? `new-${at}`} person={one} />
      ))}
      {people.length > shown.length ? (
        <button type="button" className="linkish flush" onClick={() => setAll(true)}>
          {importReplaceMore(people.length - shown.length)}
        </button>
      ) : null}
    </Group>
  );
}

/** A group that is only names, on one line: five, then the rest on request. */
function Names({
  title,
  people,
  note,
}: {
  title: string;
  people: readonly ImportReplacePerson[];
  note?: string;
}) {
  const [all, setAll] = useState(false);
  if (people.length === 0) return null;
  const shown = all ? people : people.slice(0, SHOWN);
  return (
    <Group title={title} count={people.length} note={note}>
      <div className="replace-names">{shown.map((one) => one.name).join(', ')}</div>
      {people.length > shown.length ? (
        <button type="button" className="linkish flush" onClick={() => setAll(true)}>
          {importReplaceMore(people.length - shown.length)}
        </button>
      ) : null}
    </Group>
  );
}
